import { DomainError } from '../errors.js';
import type { RevisionState } from './revision-state.js';

/**
 * Kişisel "Read & Understood" (ADR 0028 #29). Okuma gereği revizyonda (`read_required`,
 * değiştirilemez, içerik hash'ine girer), kitle dokümanda (`read_audience`, değişebilir) tutulur.
 * Kimin okuması beklendiği hesaplanır, saklanmaz. Kaptanın gemi teyidi kendi okumasını da
 * karşılar. Saf fonksiyonlar; gemi sunucusu (M8a2) ve arayüz kullanır.
 */

export const READ_AUDIENCES = ['all_vessel_roles', 'roles'] as const;
export type ReadAudience = (typeof READ_AUDIENCES)[number];

export interface ReadingRevision {
  revisionId: string;
  /** Gemide `asOf`'a göre türetilmiş durum. */
  state: RevisionState;
  readRequired: boolean;
  /** Dosyası gemide doğrulandı mı. */
  fileVerified: boolean;
  audience: ReadAudience;
  /** Kitle `roles` ise roller (`document_read_roles`). */
  audienceRoleIds: readonly string[];
  /**
   * Doküman bu gemide görünür mü (`documentVisibleOn`). Kapsamı daraltılan dokümanın revizyonları
   * gemide kalır ve `published` türer; gizlenen dokümanın okuması beklenmez (#28).
   */
  visible: boolean;
}

/**
 * Kişinin atama rollerinden biri kitlede mi. `all_vessel_roles`: o gemide `documents.view`
 * yetkisi olan roller, hesap anındaki matrise göre; sonradan eklenen rol kendiliğinden girer.
 * `roles`: seçili rollerden yalnız `documents.view` yetkisi olanlar. Dokümanı açamayan rolden
 * okuma beklenmez; beyan da `documents.view` ister. Kesişim hesap anında alınır: matris
 * değişince kitle kendiliğinden düzelir.
 */
export function inReadAudience(
  revision: ReadingRevision,
  roleIds: readonly string[],
  viewerRoleIds: readonly string[],
): boolean {
  const audience =
    revision.audience === 'roles'
      ? revision.audienceRoleIds.filter((roleId) => viewerRoleIds.includes(roleId))
      : viewerRoleIds;
  return roleIds.some((roleId) => audience.includes(roleId));
}

export interface PendingReadsInput {
  /** Kişi ve şu anki gemi atamasının rolleri; atanmamışsa boş. */
  person: { userId: string; roleIds: readonly string[] };
  revisions: readonly ReadingRevision[];
  /** O gemide `documents.view` yetkisi olan roller. */
  viewerRoleIds: readonly string[];
  /** Kişinin beyan verdiği revizyonlar. */
  confirmed: ReadonlySet<string>;
  /** Kişinin gemi teyidi (kaptan) verdiği revizyonlar: kendi okumasını karşılar. */
  acknowledged: ReadonlySet<string>;
}

/**
 * Kişinin bekleyen okumaları: doküman gemide görünür, revizyon yürürlükte, okuma gerekli, dosyası
 * doğrulanmış, kişi gemiye atanmış ve rolü kitlede, beyanı (ya da gemi teyidi) yok. Yeni katılan
 * kişi yürürlüktekileri görür; yürürlükten kalkan beklenmez.
 */
export function pendingReads(input: PendingReadsInput): string[] {
  const { person } = input;
  if (person.roleIds.length === 0) return [];
  return input.revisions
    .filter(
      (revision) =>
        revision.visible &&
        revision.state === 'published' &&
        revision.readRequired &&
        revision.fileVerified &&
        inReadAudience(revision, person.roleIds, input.viewerRoleIds) &&
        !input.confirmed.has(revision.revisionId) &&
        !input.acknowledged.has(revision.revisionId),
    )
    .map((revision) => revision.revisionId);
}

export interface ReadConfirmationCheck {
  /** Oturumdaki kişi. */
  actorId: string;
  /** Beyanın sahibi (`created_by`). */
  confirmerId: string;
  /** Kişinin şu anki gemi atamasının rolleri. */
  assignedRoleIds: readonly string[];
  revision: ReadingRevision;
  viewerRoleIds: readonly string[];
  alreadyConfirmed: boolean;
}

/**
 * Okuma beyanının kuralları (ADR 0028 #29): kendi adına (`NOT_OWN_READ`), gemiye atanmış
 * (`NOT_ASSIGNED`), doküman gemide görünür (`DOCUMENT_NOT_VISIBLE`, #28), revizyon yürürlükte
 * (`REVISION_NOT_CURRENT`), okuma gerekli ve rol kitlede (`READ_NOT_REQUIRED`), dosya doğrulanmış
 * (`FILE_NOT_VERIFIED`), ikinci beyan yok (`ALREADY_CONFIRMED`). Parolayı uç nokta ister (M8a2).
 */
export function assertReadConfirmation(check: ReadConfirmationCheck): void {
  if (check.actorId !== check.confirmerId) throw new DomainError('NOT_OWN_READ');
  if (check.assignedRoleIds.length === 0) throw new DomainError('NOT_ASSIGNED');
  if (!check.revision.visible) throw new DomainError('DOCUMENT_NOT_VISIBLE');
  if (check.revision.state !== 'published') {
    throw new DomainError('REVISION_NOT_CURRENT', check.revision.state);
  }
  if (
    !check.revision.readRequired ||
    !inReadAudience(check.revision, check.assignedRoleIds, check.viewerRoleIds)
  ) {
    throw new DomainError('READ_NOT_REQUIRED');
  }
  if (!check.revision.fileVerified) throw new DomainError('FILE_NOT_VERIFIED');
  if (check.alreadyConfirmed) throw new DomainError('ALREADY_CONFIRMED');
}

/** Geri çekme teyidi istenebilecek durumlar: geri çekilen ve iptal edilen (ADR 0028 #26, #27). */
const ENDED_STATES: readonly RevisionState[] = ['withdrawn', 'cancelled'];

export interface WithdrawalCandidate {
  revisionId: string;
  seqNo: number;
  /** Gemide `asOf`'a göre türetilmiş durum. */
  state: RevisionState;
  /** Yayın olayı var (gemiye inmiştir). */
  published: boolean;
}

/**
 * Bir gemide geri çekme teyidi bekleyen revizyonlar, yeniden eskiye (ADR 0028 #26, #27 madde 5;
 * ADR 0032 #88; inceleme domain F1). Geri çekilen ya da iptal edilen, yayınlanmış revizyon:
 * - gemide benimseme teyidi verildiyse (D'den önceki ön teyit dahil: basılı kopyalar
 *   dağıtılmış olabilir [ISM 11.2.3]), ya da
 * - yürürlükteyken geri çekildiyse (yürürlükte revizyon yok ve son yayınlanmış revizyon geri
 *   çekildi);
 * ve gemide geri çekme teyidi yoksa. Yürürlükte başka bir revizyon olsa da beklenir.
 */
export function withdrawalsAwaitingAcknowledgement(input: {
  revisions: readonly WithdrawalCandidate[];
  hasCurrent: boolean;
  acknowledged: (revisionId: string, kind: 'adopted' | 'withdrawn') => boolean;
}): string[] {
  const published = input.revisions.filter((r) => r.published).sort((a, b) => b.seqNo - a.seqNo);
  const latest = published[0];
  return published
    .filter((r) => ENDED_STATES.includes(r.state))
    .filter(
      (r) =>
        input.acknowledged(r.revisionId, 'adopted') ||
        (!input.hasCurrent && r.revisionId === latest?.revisionId && r.state === 'withdrawn'),
    )
    .filter((r) => !input.acknowledged(r.revisionId, 'withdrawn'))
    .map((r) => r.revisionId);
}
